import React, { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./LogoManagementPage.module.scss";
import Header from "../Header/header";
import SetupSteps, { Step } from "../Register/SetupSteps/SetupSteps";
import axios from "axios";
import confetti from "canvas-confetti";

const STEPS: Step[] = [
  { key: "practice", label: "Practice and personal info", path: "/practiceDetails" },
  { key: "logo", label: "Logo Management", path: "/logoManagement" },
  { key: "social", label: "Social Media Links", path: "/socialLinks" },
  { key: "portfolio", label: "Portfolio Gallery", path: "/portfolioGallery" },
  { key: "consent", label: "Consent Templates", path: "/consentTemplates" },
  { key: "preview", label: "Preview & Complete", path: "/previewComplete" },
];

type UploadState = {
  file?: File;
  previewUrl?: string;
  error?: string;
  uploading?: boolean;
};

function UploadBox({
  title,
  hint,
  accept = ".png,.jpg,.jpeg",
  uploadUrl,
  payloadKey,
  onUploaded,
}: {
  title: string;
  hint: string;
  accept?: string;
  uploadUrl: string;
  payloadKey: string;
  onUploaded?: (base64: string) => void;
}) {
  const [state, setState] = useState<UploadState>({});
  const [showModal, setShowModal] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const onChoose = () => inputRef.current?.click();

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  };

  const celebrate = () => {
    const duration = 2 * 1000;
    const animationEnd = Date.now() + duration;

    const frame = () => {
      confetti({ particleCount: 5, angle: 60, spread: 55, origin: { x: 0 } });
      confetti({ particleCount: 5, angle: 120, spread: 55, origin: { x: 1 } });
      if (Date.now() < animationEnd) requestAnimationFrame(frame);
    };

    frame();
  };

  const resetDropzone = () => {
    if (state.previewUrl) URL.revokeObjectURL(state.previewUrl);
    setState({});
    if (inputRef.current) inputRef.current.value = "";
  };

  const uploadLogo = async () => {
    if (!state.file) return;

    try {
      setState((s) => ({ ...s, uploading: true, error: undefined }));
      const base64 = await fileToBase64(state.file);

      await axios.put(
        uploadUrl,
        { [payloadKey]: base64 },
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        }
      );

      onUploaded?.(base64);

      celebrate();
      setShowModal(true);
      setTimeout(() => setShowModal(false), 3000);
      resetDropzone();
    } catch (err) {
      console.error("Upload error:", err);
      setState((s) => ({
        ...s,
        uploading: false,
        error: "Failed to upload image. Please try again.",
      }));
    } finally {
      setState((s) => ({ ...s, uploading: false }));
    }
  };

  const handleFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const f = files[0];

    if (!accept.split(",").some((ext) => f.name.toLowerCase().endsWith(ext.trim()))) {
      alert("Invalid file type.");
      return;
    }

    if (f.size > 2 * 1024 * 1024) {
      alert("Max file size is 2MB.");
      return;
    }

    if (state.previewUrl) URL.revokeObjectURL(state.previewUrl);

    const previewUrl = URL.createObjectURL(f);
    setState({ file: f, previewUrl, error: undefined, uploading: false });
  };

  const onDrop: React.DragEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault();
    handleFiles(e.dataTransfer.files);
  };

  const onChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    handleFiles(e.target.files);
  };

  useEffect(() => {
    return () => {
      if (state.previewUrl) URL.revokeObjectURL(state.previewUrl);
    };
  }, [state.previewUrl]);

  return (
    <div className={styles.uploadCard}>
      {showModal && (
        <div className={styles.modalBackdrop}>
          <div className={styles.modal}>
            <div className={styles.celebrationIcon}>🎉</div>
            <h3 className={styles.modalTitle}>Upload successful!</h3>
            <p className={styles.modalSubtitle}>Your image has been saved.</p>
          </div>
        </div>
      )}

      <div
        className={styles.dropArea}
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
        role="button"
        tabIndex={0}
        aria-label={title}
      >
        <div className={styles.iconWrap} aria-hidden="true">
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
            <path d="M12 16V4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            <path
              d="M7 9l5-5 5 5"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path d="M4 20h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </div>

        <div className={styles.uploadTitle}>{title}</div>
        <div className={styles.uploadHint}>{hint}</div>

        <button type="button" className={styles.chooseBtn} onClick={onChoose}>
          Choose file
        </button>

        <input
          ref={inputRef}
          type="file"
          accept={accept}
          className={styles.fileInput}
          onChange={onChange}
        />

        {state.previewUrl && (
          <div className={styles.previewWrap}>
            <div className={styles.previewFrame}>
              <img src={state.previewUrl} alt="Preview" className={styles.previewImg} />
            </div>

            {state.error && <div className={styles.errorText}>{state.error}</div>}

            <div className={styles.previewActions}>
              <button
                type="button"
                className={styles.btnSecondary}
                onClick={resetDropzone}
                disabled={state.uploading}
              >
                Cancel
              </button>

              <button
                type="button"
                className={styles.btnPrimary}
                onClick={uploadLogo}
                disabled={state.uploading}
              >
                {state.uploading ? "Uploading..." : "Upload"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function LogoManagementPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [logoPreview, setLogoPreview] = useState<string>("");

  useEffect(() => {
    const fetchUserAndLogo = async () => {
      try {
        const { data } = await axios.get("/user");
        let currentUser: any = null;

        if (Array.isArray(data) && data.length > 1) {
          currentUser = data[1];
          setUser(currentUser);
        } else if (data && typeof data === "object" && !Array.isArray(data)) {
          currentUser = data;
          setUser(currentUser);
        }

        const endpoint =
          currentUser?.level === "user"
            ? `${process.env.REACT_APP_BASE_URL_V2}onboarding/get_practitioner_data`
            : `${process.env.REACT_APP_BASE_URL_V2}onboarding/get_practice_data`;

        const res = await axios.get(endpoint, {
          headers: { Accept: "application/json" },
        });

        const logoFromApi =
          res?.data?.logo ||
          res?.data?.logo_url ||
          res?.data?.practice_logo ||
          res?.data?.practice_logo_url ||
          res?.data?.practitioner_logo ||
          res?.data?.practitioner_logo_url ||
          res?.data?.profile_logo ||
          res?.data?.profile_logo_url ||
          res?.data?.practice?.logo ||
          res?.data?.practice?.logo_url ||
          res?.data?.practitioner?.logo ||
          res?.data?.practitioner?.logo_url ||
          "";

        if (typeof logoFromApi === "string" && logoFromApi.trim()) {
          setLogoPreview(logoFromApi.trim());
        } else {
          setLogoPreview("");
        }
      } catch (err) {
        console.error("Error fetching user/logo:", err);
      }
    };

    fetchUserAndLogo();
  }, []);

  return (
    <div className={styles.page}>
      <Header />

      <main className={styles.main}>
        <SetupSteps
          steps={STEPS}
          current={2}
          rightLabel="10% Complete"
          onStepClick={(step) => navigate(step.path)}
        />

        <section className={styles.hero}>
          <div className={styles.heroContent}>
            <h1 className={styles.title}>Logo management</h1>
            <p className={styles.subtitle}>
              Add your practice logo or personal brand to make your portal truly yours.
              Your logo will appear on consent forms and patient materials, helping you
              look polished, consistent, and professional in every communication.
            </p>
          </div>

          <div className={styles.progressCard}>
            <span className={styles.progressValue}>Step 2</span>
            <span className={styles.progressLabel}>Brand setup</span>
          </div>
        </section>

        {user?.level !== "user" && (
          <section className={styles.card}>
            <div className={styles.cardHeader}>
              <div>
                <h2 className={styles.cardTitle}>Practice logo*</h2>
                <p className={styles.cardSubtitle}>
                  Upload the main logo used for your practice branding.
                </p>
              </div>
            </div>

            {logoPreview ? (
              <div className={styles.currentLogoBlock}>
                <span className={styles.currentLogoLabel}>Current logo</span>
                <div className={styles.currentLogoPreview}>
                  <img
                    src={logoPreview}
                    alt="Practice logo preview"
                    className={styles.currentLogoImage}
                    onError={() => setLogoPreview("")}
                  />
                </div>
              </div>
            ) : null}

            <UploadBox
              title="Upload practice logo"
              hint="PNG or JPG, max 2MB, 200x200px minimum"
              uploadUrl={`${process.env.REACT_APP_BASE_URL_V2}onboarding/update_practice_data`}
              payloadKey="logo"
              onUploaded={(base64) => setLogoPreview(base64)}
            />
          </section>
        )}

        {user?.level === "user" && (
          <section className={styles.card}>
            <div className={styles.cardHeader}>
              <div>
                <h2 className={styles.cardTitle}>Personal logo</h2>
                <p className={styles.cardSubtitle}>
                  Upload your personal logo or professional headshot for practitioner branding.
                </p>
              </div>
            </div>

            {logoPreview ? (
              <div className={styles.currentLogoBlock}>
                <span className={styles.currentLogoLabel}>Current logo</span>
                <div className={styles.currentLogoPreview}>
                  <img
                    src={logoPreview}
                    alt="Personal logo preview"
                    className={styles.currentLogoImage}
                    onError={() => setLogoPreview("")}
                  />
                </div>
              </div>
            ) : null}

            <UploadBox
              title="Upload personal logo or professional headshot"
              hint="PNG or JPG, max 2MB, 200x200px minimum"
              uploadUrl={`${process.env.REACT_APP_BASE_URL_V2}onboarding/update_practitioner_data`}
              payloadKey="logo"
              onUploaded={(base64) => setLogoPreview(base64)}
            />

            <div className={`${styles.tipCard} ${styles.extraMargin}`}>
              <h3 className={styles.tipTitle}>A few tips for the best results:</h3>
              <ul className={styles.tips}>
                <li>Use a high-resolution image so your logo looks sharp across all platforms.</li>
                <li>
                  Remember, your logo will appear on consent forms and patient communications,
                  which helps reinforce your brand.
                </li>
                <li>Choose a logo that’s versatile and professional so it works well in different formats.</li>
                <li>You can update your logo anytime to keep your branding fresh and consistent.</li>
              </ul>
            </div>
          </section>
        )}

        <div className={styles.actions}>
          <button
            type="button"
            className={styles.btnSecondary}
            onClick={() => navigate("/practiceDetails")}
          >
            Back
          </button>
          <button
            type="button"
            className={styles.btnPrimary}
            onClick={() => navigate("/socialLinks")}
          >
            Next
          </button>
        </div>
      </main>

      <footer className={styles.footerBar}>
        Copyright © Medicube 2025. All Rights Reserved.
      </footer>
    </div>
  );
}